import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ProcessedInvoiceTable from './components/ProcessedInvoiceTable';

const ProcessedInvoicesPage = () => {
  const { user, currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [processedInvoices, setProcessedInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProcessedInvoices = async () => {
      if (!user?.id || !currentOrgId) return;
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('parsed_invoices')
          .select(`
            *,
            documents!inner(
              file_name,
              storage_path
            ),
            user_profiles (
              full_name,
              email
            )
          `)
          .eq('org_id', currentOrgId)
          .eq('status', 'exported')
          .order('updated_at', { ascending: false });

        if (fetchError) throw fetchError;

        const transformed = (data || [])
          .map(inv => {
            const netAmount = parseFloat(inv.net_amount || 0);
            const vatAmount = parseFloat(inv.vat_amount || 0);
            const totalAmount = netAmount + vatAmount;
            const exportTimestamp = inv.exported_at || inv.updated_at || inv.created_at;
            const exportDate = exportTimestamp ? new Date(exportTimestamp) : null;
          const exportDateISO = exportDate ? exportDate.toISOString().slice(0, 10) : null;
          const exportedOnLabel = exportDate
            ? exportDate.toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : '—';

          return {
            id: inv.id,
              vendor: inv.vendor_name,
              invoiceNumber: inv.invoice_number,
              filename: inv.documents?.file_name,
              documentPath: inv.documents?.storage_path,
              totalAmount,
              currency: inv.currency || 'GBP',
              exportedOnLabel,
              exportDateISO,
            exportedBy:
              inv?.exported_by ||
              inv?.user_profiles?.full_name ||
              inv?.user_profiles?.email ||
              'System',
            exportTimestampValue: exportDate ? exportDate.getTime() : 0,
          };
          })
          ?.sort((a, b) => b.exportTimestampValue - a.exportTimestampValue);

        setProcessedInvoices(transformed);
      } catch (err) {
        console.error('Error fetching processed invoices:', err);
        setError('Unable to load processed invoices. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProcessedInvoices();
  }, [user?.id, currentOrgId]);

  useEffect(() => {
    if (!selectedDate) {
      setFilteredInvoices(processedInvoices);
      return;
    }

    setFilteredInvoices(
      processedInvoices?.filter(inv => inv?.exportDateISO === selectedDate)
    );
  }, [processedInvoices, selectedDate]);

  const totalForSelectedDate = useMemo(() => {
    return filteredInvoices?.reduce((sum, inv) => sum + (inv?.totalAmount || 0), 0);
  }, [filteredInvoices]);

  const handleOpenInvoice = (invoiceId) => {
    if (!invoiceId) return;
    navigate('/invoice-editor', {
      state: {
        invoiceId,
        viewOnly: true,
      }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Archive" size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Processed Invoices</h1>
                <p className="text-sm text-muted-foreground">
                  Review all Sage exports by date and operator.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Export date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e?.target?.value)}
                className="w-full md:w-64 h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>

            <div className="flex items-center space-x-3">
              {selectedDate && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedDate('')}>
                  Clear date
                </Button>
              )}
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Invoices shown</p>
                <p className="text-lg font-semibold text-foreground">{filteredInvoices?.length}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total value</p>
                <p className="text-lg font-semibold text-foreground">
                  {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(totalForSelectedDate || 0)}
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-destructive/10 text-destructive text-sm px-4 py-3 rounded-md border border-destructive/20">
              {error}
            </div>
          )}

          <ProcessedInvoiceTable
            invoices={filteredInvoices}
            isLoading={isLoading}
            onOpenInvoice={handleOpenInvoice}
          />
        </div>
      </main>
    </div>
  );
};

export default ProcessedInvoicesPage;
